import {
    LOAD_ALL_SELLERS_FAIL,
    LOAD_ALL_SELLERS_REQUEST,
    LOAD_ALL_SELLERS_SUCCESS,
    LOAD_CATALOG_AD_SUCCESS,
    LOAD_CATALOGS_SUCCESS,
    LOAD_HOME_DATA_SUCCESS,
    LOAD_ITEMS_SUCCESS,
    LOAD_SELLERS_FAIL,
    LOAD_SELLERS_REQUEST,
    LOAD_SELLERS_SUCCESS,
    LOAD_SIMILAR_ITEMS_V3_SUCCESS,
    LOAD_SIMILAR_SOLD_ITEMS_V3_SUCCESS,
    LOAD_UPCOMING_CATALOGS_SUCCESS,
} from './actions';
import { loadBatchEnd, loadBatchStart } from './fetchBatch';
import api from '@/redux/api/seller';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import groupBy from 'lodash/groupBy';
import ms from 'ms';
import union from 'lodash/union';
import { whichSellerCatalogCountsNeeded } from './sellerCatalogCounts';

const REDUX_STORE_TIME = ms('30m');
const FETCH_SET_SIZE = 120;

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loadedSellers: false,
    loading: [],
    loadingSellers: false,
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loadedSellers;
    let loading;
    let loadingSellers;
    let time;

    switch (action.type) {
        case LOAD_ALL_SELLERS_FAIL:
            return {
                ...state,
                loadingSellers: false,
            };
        case LOAD_ALL_SELLERS_REQUEST:
            return {
                ...state,
                loadingSellers: true,
            };
        case LOAD_SELLERS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, action.meta.sellerIds),
            };
        case LOAD_SELLERS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, action.payload),
            };
        case LOAD_SIMILAR_ITEMS_V3_SUCCESS:
        case LOAD_SIMILAR_SOLD_ITEMS_V3_SUCCESS:
            existing = cloneDeep(state.byId);
            action.payload.forEach(({ seller }) => {
                if (!existing[seller.sellerId]) {
                    existing[seller.sellerId] = seller;
                }
            });
            return {
                ...state,
                byId: existing,
            };
        case LOAD_ALL_SELLERS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;
            action.payload.forEach((item) => {
                existing[item.sellerId] = { ...item };
                loaded[item.sellerId] = time;
                loading = difference(loading, [item.sellerId]);
            });
            loadingSellers = false;
            loadedSellers = true;
            return {
                ...state,
                byId: existing,
                loaded,
                loadedSellers,
                loading,
                loadingSellers,
            };
        case LOAD_CATALOG_AD_SUCCESS:
        case LOAD_CATALOGS_SUCCESS:
        case LOAD_HOME_DATA_SUCCESS:
        case LOAD_ITEMS_SUCCESS:
        case LOAD_SELLERS_SUCCESS:
        case LOAD_UPCOMING_CATALOGS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (action.payload.sellers) {
                action.payload.sellers.forEach((item) => {
                    existing[item.sellerId] = { ...item };
                    loaded[item.sellerId] = time;
                    loading = difference(loading, [item.sellerId]);
                });
            }

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state) => state.seller;
const idSelector = (state, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

const areAllSellersLoaded = createSelector(stateSelector, (state) => state.loadedSellers || false);

export const areAllSellersLoading = createSelector(stateSelector, (state) => state.loadingSellers || false);

const getAllSellers = createSelector(byIdSelector, (byId) => Object.keys(byId).map((key) => byId[key]));

// This is pretty gross.  Hopefully we can just get the correct form of data
const generateStateLists = (sellers: any = {}) => {
    const europeSellers = sellers.europe || [];
    const usSellers = sellers.us || [];
    const worldSellers = sellers.world || [];

    const europeSellerByState = groupBy(
        europeSellers.sort((a, b) => {
            if (a.country.toLowerCase() > b.country.toLowerCase()) {
                return 1;
            }
            return -1;
        }),
        (seller) => seller.countryCode
    );
    const usSellersByState = groupBy(
        usSellers.sort((a, b) => {
            if (a.stateName.toLowerCase() > b.stateName.toLowerCase()) {
                return 1;
            }
            return -1;
        }),
        (seller) => seller.stateCode
    );
    const worldSellersByState = groupBy(
        worldSellers.sort((a, b) => {
            if (a.country.toLowerCase() > b.country.toLowerCase()) {
                return 1;
            }
            return -1;
        }),
        (seller) => seller.countryCode
    );

    const europe = [];
    Object.keys(europeSellerByState).forEach((key) => {
        if (!europeSellerByState[key][0].country) {
            return;
        }
        const country = {
            name: europeSellerByState[key][0].country,
            sellers: europeSellerByState[key]
                .map((seller) => {
                    return { name: seller.name, sellerId: seller.sellerId };
                })
                .sort((a, b) => {
                    if (a.name.toLowerCase() > b.name.toLowerCase()) {
                        return 1;
                    }
                    return -1;
                }),
        };
        europe.push(country);
    });

    const us = [];
    Object.keys(usSellersByState).forEach((key) => {
        if (!usSellersByState[key][0].stateName) {
            return;
        }
        const state = {
            name: usSellersByState[key][0].stateName,
            sellers: usSellersByState[key]
                .map((seller) => {
                    return { name: seller.name, sellerId: seller.sellerId };
                })
                .sort((a, b) => {
                    if (a.name.toLowerCase() > b.name.toLowerCase()) {
                        return 1;
                    }
                    return -1;
                }),
        };
        us.push(state);
    });

    const world = [];
    Object.keys(worldSellersByState).forEach((key) => {
        if (!worldSellersByState[key][0].country) {
            return;
        }
        const country = {
            name: worldSellersByState[key][0].country,
            sellers: worldSellersByState[key]
                .map((seller) => {
                    return { name: seller.name, sellerId: seller.sellerId };
                })
                .sort((a, b) => {
                    if (a.name.toLowerCase() > b.name.toLowerCase()) {
                        return 1;
                    }
                    return -1;
                }),
        };
        world.push(country);
    });

    return {
        europe,
        us,
        world,
    };
};

const getFilteredAllSellers = createSelector(getAllSellers, (allSellers) =>
    allSellers.filter((x) => {
        // Premiere Props has 3 seller ids now, filter out the first two from the directory page
        return x.sellerId !== 675 && x.sellerId !== 759;
    })
);

export const getAllSellersForDirectoryPage = createSelector(getFilteredAllSellers, (allSellers) => {
    const sellers = groupBy(allSellers, (seller) => seller.regionCode);
    return generateStateLists(sellers);
});

export const getMatchingSellersFunction = (state: GlobalState) => {
    return (query: any) => {
        const sellers = getFilteredAllSellers(state);
        return sellers.filter((seller) => {
            const searchTokens = query.toLowerCase().split(/\s/);
            const matchingTokens = searchTokens.filter((token) => {
                const { name = '' } = seller;
                return `${name}`.toLowerCase().indexOf(token) !== -1;
            });
            const doesMatch = matchingTokens.length === searchTokens.length;
            return doesMatch;
        });
    };
};

export const shouldFetchSellers = createSelector(
    [areAllSellersLoaded, areAllSellersLoading],
    (allSellersLoaded, allSellersLoading) => !allSellersLoaded && !allSellersLoading
);

const getLoadTimeForSeller = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const isSellerLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

export const getSeller: (state: any, selector: any) => any = createSelector(
    [byIdSelector, idSelector],
    (byId, id) => byId[id] || {}
);

export const getSellerInArtaBeta: (state: any, selector: any) => any = createSelector(
    [byIdSelector, idSelector],
    (byId, sellerId) => {
        const betas = byId?.[sellerId]?.betas || [];
        return betas.includes(9);
    }
);

export const shouldFetchSeller = (state: GlobalState, sellerId: number) => {
    if (!sellerId) {
        return false;
    }
    const item = getSeller(state, sellerId);
    if (item) {
        const loaded = getLoadTimeForSeller(state, sellerId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isSellerLoading(state, sellerId);
    return !loading;
};

const whichSellersNeeded = (state, sellerIds) => sellerIds.filter((sellerId) => shouldFetchSeller(state, sellerId));

/* ACTION CREATORS */
// all sellers
const loadAllSellers = () => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);

        dispatch({
            type: LOAD_ALL_SELLERS_REQUEST,
        });

        const response = await api.fetchSellers({ deployment });
        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: LOAD_ALL_SELLERS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_ALL_SELLERS_FAIL,
        });
    }
};

export const fetchAllSellersIfNeeded = () => async (dispatch: Function, getState: Function) => {
    if (shouldFetchSellers(getState())) {
        return dispatch(loadAllSellers());
    }
    return Promise.resolve();
};

// seller
const loadSellers = (sellerIds, loadingId, isFinalFetch) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            payload: sellerIds,
            type: LOAD_SELLERS_REQUEST,
        });
        const response = await api.fetchSellersByIds({ authToken, deployment, sellerIds });
        dispatch({
            meta: { actionTime: Date.now(), sellerIds },
            payload: response.data,
            type: LOAD_SELLERS_SUCCESS,
        });
        if (isFinalFetch) {
            dispatch(loadBatchEnd(loadingId));
        }
    } catch (error) {
        dispatch({
            error: true,
            meta: { sellerIds },
            payload: error,
            type: LOAD_SELLERS_FAIL,
        });
        dispatch(loadBatchEnd(loadingId));
    }
};

const loadSellersInSets = (ids, setSize, loadingId) => {
    return (dispatch) => {
        if (ids.length > setSize) {
            return dispatch(loadSellers(ids.slice(0, setSize), loadingId, false)).then(() => {
                dispatch(loadSellersInSets(ids.slice(setSize), setSize, loadingId));
            });
        }
        return dispatch(loadSellers(ids, loadingId, true));
    };
};

export const fetchSellersIfNeeded = (sellerIds: number[], loadingId: string = 'none') => async (
    dispatch: Function,
    getState: Function
) => {
    const neededSellers = whichSellersNeeded(getState(), sellerIds);
    const neededCatalogCounts = whichSellerCatalogCountsNeeded(getState(), sellerIds);
    const needed = union(neededSellers, neededCatalogCounts);
    if (needed.length) {
        dispatch(loadBatchStart(loadingId));
        return dispatch(loadSellersInSets(needed, FETCH_SET_SIZE, loadingId));
    }
    return Promise.resolve();
};
